import { useState, useCallback, useEffect, useRef } from 'react';
import { useT } from '../hooks/useT';
import { Markdown } from './Markdown';
import { useHelpManifest, useHelpTopic } from '../hooks/useHelp';

/** Drops the first non-empty line if it is a markdown H1 — the panel header already shows the title. */
function stripLeadingH1(md: string): string {
  const lines = md.split('\n');
  const idx = lines.findIndex((l) => l.trim() !== '');
  if (idx !== -1 && lines[idx].startsWith('# ')) {
    return lines.slice(idx + 1).join('\n');
  }
  return md;
}

export function HelpButton({ token }: { token: string }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const { data: manifest } = useHelpManifest(token);
  const { data: topic } = useHelpTopic(token, selected);
  const dialogRef = useRef<HTMLElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    closeBtnRef.current?.focus();

    const el = dialogRef.current;
    if (!el) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        el.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  const consumerTopics = manifest?.sets.find((s) => s.key === 'consumer')?.topics ?? [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('help.open')}
        title={t('help.open')}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50"
      >
        ?
      </button>
      {open && (
        <div className="fixed inset-0 z-30 flex justify-end bg-black/30" onClick={(e) => e.target === e.currentTarget && close()}>
          <aside
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('help.title')}
            className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">
                {selected && topic?.title ? topic.title : t('help.title')}
              </h2>
              <button
                ref={closeBtnRef}
                type="button"
                onClick={close}
                aria-label={t('help.close')}
                className="rounded border border-slate-300 px-2 py-1 text-sm"
              >
                ✕
              </button>
            </div>
            {!selected && (
              <ul className="space-y-1">
                {consumerTopics.map((tp) => (
                  <li key={tp.id}>
                    <button type="button" onClick={() => setSelected(tp.id)} className="text-left text-sm text-brand underline">{tp.title}</button>
                  </li>
                ))}
              </ul>
            )}
            {selected && (
              <div>
                <button type="button" onClick={() => setSelected(null)} className="mb-3 text-xs text-slate-500 underline">← {t('help.title')}</button>
                {topic && <Markdown source={stripLeadingH1(topic.bodyMarkdown)} />}
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
